import React, { useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useFairTracker } from '../data/FairTrackerStore';
import { Input, Select, SupplierCard, Empty, Pill, Thumb, Stars } from '../ui';
import { matchesSearch, priceLine, num, mainPhoto, pendingFollowUps } from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * My Suppliers (spec § 5.5).
 *
 * ONE ROW PER PRODUCT, not per supplier — a supplier with three products
 * appears three times. That is deliberate: the buyer is comparing products, and
 * a supplier who quoted three different lamps is three different decisions.
 *
 * Every filter lives in the query string so the dashboard tiles can link
 * straight into a filtered view, and so a filtered list is shareable/back-able.
 */
export default function SuppliersScreen() {
  const history = useHistory();
  const location = useLocation();
  const { fairs, fair, visibleSuppliers, currentFair } = useFairTracker();

  const params = new URLSearchParams(location.search);
  const [query, setQuery] = useState('');

  const get = (k) => params.get(k) || '';
  const setParam = (k, v) => {
    const next = new URLSearchParams(location.search);
    if (v) next.set(k, v);
    else next.delete(k);
    history.replace(`${p('/suppliers')}?${next.toString()}`);
  };
  const toggleParam = (k, v) => setParam(k, get(k) === v ? '' : v);

  const decision = get('d');
  const sampleOnly = get('sample') === '1';
  const typeFilter = get('type');
  const category = get('cat');
  const followUpOnly = get('fu') === '1';
  const revisitOnly = get('rv') === '1';
  const sort = get('sort') || 'recent';
  const view = get('view') || 'cards';

  /** Flatten to product rows, then filter. A supplier with no products still
   *  appears once, so a company captured without a product isn't invisible. */
  const rows = useMemo(() => {
    const out = [];
    visibleSuppliers.forEach((s) => {
      const products = (s.products || []).length ? s.products : [null];
      products.forEach((prod) => out.push({ supplier: s, product: prod }));
    });
    return out;
  }, [visibleSuppliers]);

  const categories = useMemo(() => {
    const set = new Set();
    visibleSuppliers.forEach((s) => (s.products || []).forEach((x) => x.category && set.add(x.category)));
    return Array.from(set).sort();
  }, [visibleSuppliers]);

  const filtered = useMemo(() => {
    let list = rows;

    if (query.trim()) list = list.filter(({ supplier }) => matchesSearch(supplier, query));
    if (decision) list = list.filter(({ product }) => product && product.decision === decision);
    if (sampleOnly) {
      list = list.filter(({ product }) => product && (product.sample === 'Ordered' || product.sample === 'Received'));
    }
    if (typeFilter) list = list.filter(({ supplier }) => supplier.type === typeFilter);
    if (category) list = list.filter(({ product }) => product && product.category === category);
    if (revisitOnly) list = list.filter(({ product }) => product && product.revisit);
    if (followUpOnly) list = list.filter(({ supplier }) => pendingFollowUps(supplier) > 0);

    const priceOf = ({ product }) => {
      if (!product || !(product.price || product.bestPrice)) return Number.POSITIVE_INFINITY;
      return num(product.bestPrice || product.price);
    };

    const sorted = [...list];
    if (sort === 'rating') {
      sorted.sort((a, b) => ((b.product && b.product.rating) || 0) - ((a.product && a.product.rating) || 0));
    } else if (sort === 'price') {
      sorted.sort((a, b) => priceOf(a) - priceOf(b));
    } else if (sort === 'supplier') {
      sorted.sort((a, b) => (a.supplier.company || '').localeCompare(b.supplier.company || ''));
    } else if (sort === 'moq') {
      sorted.sort((a, b) => num(a.product && a.product.moq) - num(b.product && b.product.moq));
    } else {
      sorted.sort((a, b) => (b.supplier.createdAt || 0) - (a.supplier.createdAt || 0));
    }
    return sorted;
  }, [rows, query, decision, sampleOnly, typeFilter, category, revisitOnly, followUpOnly, sort]);

  const fairNameFor = (s) => {
    if (currentFair !== 'all') return null;
    const f = fairs.find((x) => x.id === s.fairId);
    return f ? f.name : null;
  };

  const chip = (label, active, onClick) => (
    <button key={label} type="button" className={`ft-chip${active ? ' ft-chip-on' : ''}`} onClick={onClick}>
      {tr(label)}
    </button>
  );

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("My Suppliers")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {tr(`${filtered.length} result${filtered.length === 1 ? '' : 's'}`)} · {tr('one row per product')}
        </p>
      </div>

      <Input value={query} onChange={setQuery} placeholder={tr("Search…")} aria-label="Search suppliers" />

      <div className="ft-chips" style={{ marginTop: 10 }}>
        {chip('Strong', decision === 'Strong', () => toggleParam('d', 'Strong'))}
        {chip('Maybe', decision === 'Maybe', () => toggleParam('d', 'Maybe'))}
        {chip('No', decision === 'No', () => toggleParam('d', 'No'))}
        {chip('Sample', sampleOnly, () => setParam('sample', sampleOnly ? '' : '1'))}
        {chip('Manufacturer', typeFilter === 'Manufacturer', () => toggleParam('type', 'Manufacturer'))}
        {chip('Trading co.', typeFilter === 'Trading Company', () => toggleParam('type', 'Trading Company'))}
        {chip('Revisit', revisitOnly, () => setParam('rv', revisitOnly ? '' : '1'))}
        {chip('Follow-up pending', followUpOnly, () => setParam('fu', followUpOnly ? '' : '1'))}
      </div>

      <div className="ft-grid-2">
        {categories.length > 0 && (
          <Select
            value={category}
            placeholder={tr("All categories")}
            options={categories}
            onChange={(v) => setParam('cat', v)}
          />
        )}
        <Select
          value={sort}
          options={[
            { value: 'recent', label: 'Recently added' },
            { value: 'rating', label: 'Rating' },
            { value: 'price', label: tr('Lowest price') },
            { value: 'supplier', label: 'Supplier' },
            { value: 'moq', label: 'MOQ' },
          ]}
          onChange={(v) => setParam('sort', v)}
        />
      </div>

      <div className="ft-row" style={{ marginTop: 8 }}>
        <button
          type="button"
          className={`ft-btn${view === 'cards' ? ' ft-btn-primary' : ''}`}
          onClick={() => setParam('view', 'cards')}
        >
          {tr("Cards")}
        </button>
        <button
          type="button"
          className={`ft-btn${view === 'table' ? ' ft-btn-primary' : ''}`}
          onClick={() => setParam('view', 'table')}
        >
          {tr("Table")}
        </button>
      </div>

      {!filtered.length && (
        <Empty title={tr("Nothing matches those filters")} action={
          <Link className="ft-btn" to={p('/suppliers')}>{tr("Clear filters")}</Link>
        } />
      )}

      {view === 'table' && filtered.length > 0 ? (
        <div className="ft-tablewrap">
          <table className="ft-table">
            <thead>
              <tr>
                <th aria-label={tr("Photo")} />
                <th>{tr("Supplier")}</th>
                <th>{tr("Product")}</th>
                <th>{tr("Price")}</th>
                <th>{tr("MOQ")}</th>
                <th>{tr("Rating")}</th>
                <th>{tr("Decision")}</th>
                <th>{tr("Next step")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(({ supplier, product }) => (
                <tr
                  key={`${supplier.id}-${product ? product.id : 'none'}`}
                  onClick={() => history.push(p(`/s/${supplier.id}`))}
                >
                  <td>
                    <Thumb photo={mainPhoto(supplier, product && product.id)} initials={supplier.company} size={34} />
                  </td>
                  <td>
                    <b>{supplier.company}</b>
                    {supplier.booth && <span className="ft-faint"> · {supplier.booth}</span>}
                  </td>
                  <td>{product ? product.name : '—'}</td>
                  <td className="ft-num">{product ? priceLine(product, fair) || '—' : '—'}</td>
                  <td className="ft-num">{(product && product.moq) || '—'}</td>
                  <td>{product && product.rating ? <Stars value={product.rating} readOnly /> : '—'}</td>
                  <td>{product ? <Pill>{product.decision}</Pill> : '—'}</td>
                  <td>{product && product.nextStep !== 'None' ? tr(product.nextStep) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ft-list" style={{ marginTop: 12 }}>
          {filtered.map(({ supplier, product }) => (
            <SupplierCard
              key={`${supplier.id}-${product ? product.id : 'none'}`}
              supplier={supplier}
              product={product}
              fair={fair}
              fairName={fairNameFor(supplier)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
